/**
 * SearchModeToggle.tsx
 * ─────────────────────
 * Pill toggle Libre ↔ Guidée IA — partagé sur toutes les pages de recherche.
 * Persiste le choix dans localStorage (clé "sl_search_mode").
 */

import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

export type SearchMode = 'libre' | 'guidee';

const LS_KEY = 'sl_search_mode';

export function useSearchMode(
  mode: SearchMode,
  setMode: (m: SearchMode) => void,
  defaultMode: SearchMode = 'libre',
) {
  // Restore depuis localStorage au mount
  useEffect(() => {
    const saved = localStorage.getItem(LS_KEY) as SearchMode | null;
    if (saved === 'libre' || saved === 'guidee') setMode(saved);
    else setMode(defaultMode);
  }, []);

  const toggle = (m: SearchMode) => {
    setMode(m);
    localStorage.setItem(LS_KEY, m);
  };

  return toggle;
}

interface SearchModeToggleProps {
  mode:     SearchMode;
  onChange: (m: SearchMode) => void;
  /** Afficher le lien "Recherche avancée →" (jurisprudence seulement) */
  advancedHref?: string;
}

export function SearchModeToggle({ mode, onChange, advancedHref }: SearchModeToggleProps) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-end mb-3 gap-4">
      {/* Pill toggle */}
      <div className="inline-flex items-center rounded-lg border border-border bg-muted/50 p-0.5 gap-0.5">
        <button
          type="button"
          onClick={() => onChange('guidee')}
          className={`px-3 py-1 rounded-md text-[11px] font-semibold transition-all ${
            mode === 'guidee'
              ? 'bg-background text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          {t('search.mode.guided')}
        </button>
        <button
          type="button"
          onClick={() => onChange('libre')}
          className={`px-3 py-1 rounded-md text-[11px] font-semibold transition-all ${
            mode === 'libre'
              ? 'bg-background text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          {t('search.mode.advanced')}
        </button>
      </div>

      {/* Lien recherche avancée (optionnel) */}
      {advancedHref && (
        <a
          href={advancedHref}
          className="text-[11px] text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
        >
          {t('search.advanced_search')}
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7"/>
          </svg>
        </a>
      )}
    </div>
  );
}
